import React from 'react'
import {
  Box,
  Grid,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  FormControl,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
  Grow,
  FormHelperText,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import Visibility from '@mui/icons-material/Visibility'
import { useSnackbar } from 'notistack'
import {
  Link as RouterLink,
  useLocation,
  useSearchParams,
} from 'react-router-dom'

import UserService from 'api/services/User'
import { EMAIL_REGEX, PASSWORD_REGEX } from 'helpers/constants'
import usePageView from 'hooks/usePageView'

interface FieldsForSignUp {
  username: string
  email: string
  password: string
  firstName: string
  lastName: string
  mobileNumber: string
  agreement: boolean
}
interface State {
  showPassword: boolean
}
function SignupUser() {
  usePageView()

  const location = useLocation()
  const [searchParams] = useSearchParams()
  const FROM = searchParams.get(`from`)

  const { executeRecaptcha } = useGoogleReCaptcha()

  const createGoogleToken = React.useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available')
      return
    }
    return await executeRecaptcha()
  }, [executeRecaptcha])

  const [local, setLocal] = React.useState<State>({
    showPassword: false,
  })

  const { control, handleSubmit } = useForm<FieldsForSignUp>({
    defaultValues: {
      email: '',
      username: '',
      password: '',
      firstName: '',
      lastName: '',
      mobileNumber: '',
      agreement: false,
    },
  })

  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const createUserMutation = UserService.hooks.useCreateUser({
    onError: () => {
      enqueueSnackbar(t('signUpPage.failedMessage'), {
        variant: 'error',
      })
    },
  })
  const onSubmit = async (data) => {
    const token = await createGoogleToken()

    createUserMutation.mutate({ ...data, email: data?.username, token })
  }

  const handleClickShowPassword = () => {
    setLocal({
      ...local,
      showPassword: !local.showPassword,
    })
  }

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault()
  }
  return (
    <>
      <Grow in={createUserMutation.isSuccess} unmountOnExit>
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'center', sm: 'center' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            width: '100%',
          }}
        >
          <Grid
            container
            component="form"
            direction="row"
            rowSpacing={4}
            sx={{
              width: (theme) => theme.breakpoints.values.sm,
              border: 1,
              borderColor: grey[300],
              boxShadow: 25,
              borderRadius: 2.5,
              mt: 5,
              p: 6,
              bgcolor: 'background.paper',
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" textAlign="center">
                {t('userSignUpPage.successComponent.title')}
              </Typography>
              <Typography variant="body1" textAlign="center">
                {t('userSignUpPage.successComponent.subTitle')}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Link
                component={RouterLink}
                to={`/login${FROM ? location.search : ''}`}
                underline="none"
              >
                <Button variant="contained" sx={{ width: '100%' }}>
                  <Typography fontSize={20}>
                    {t('userSignUpPage.successComponent.goToLogin')}
                  </Typography>
                </Button>
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Grow>

      <Grow in={!createUserMutation.isSuccess} unmountOnExit>
        <Box sx={{ justifyContent: 'center', display: 'flex', pb: 5 }}>
          <Grid
            container
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            direction="row"
            rowSpacing={3}
            sx={{
              maxWidth: (theme) => theme.breakpoints.values.lg,
              border: 1,
              borderColor: grey[300],
              boxShadow: 25,
              borderRadius: 2.5,
              mt: 5,
              p: 6,
              bgcolor: 'background.paper',
            }}
          >
            <Grid item container xs={12}>
              <Grid item xs={12}>
                <Typography variant="h3" textAlign="center">
                  {t('userSignUpPage.title')}
                </Typography>
              </Grid>
            </Grid>
            <Grid item container direction="row" spacing={2}>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    rules={{
                      required: t(
                        'validation.fieldRequired'
                      ) as unknown as string,
                      pattern: {
                        value: EMAIL_REGEX,
                        message: t('validation.emailPatternError' as string),
                      },
                    }}
                    name="username"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label={t('userSignUpPage.email') + '*'}
                        variant="filled"
                        error={!!error}
                        helperText={error ? error?.message : ' '}
                        fullWidth
                        sx={{
                          color: 'common.black',
                          borderColor: grey[300],
                        }}
                        {...field}
                      ></TextField>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    rules={{
                      required: t(
                        'validation.fieldRequired'
                      ) as unknown as string,
                      pattern: {
                        value: PASSWORD_REGEX,
                        message: t('error.password'),
                      },
                    }}
                    name="password"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label={t('userSignUpPage.password') + '*'}
                        variant="filled"
                        error={!!error}
                        type={local.showPassword ? 'text' : 'password'}
                        fullWidth
                        helperText={error ? error?.message : ' '}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                color={
                                  local.showPassword ? 'primary' : 'default'
                                }
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                              >
                                {local.showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          color: 'common.black',
                          borderColor: grey[300],
                        }}
                        {...field}
                      ></TextField>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    rules={{
                      required: t(
                        'validation.fieldRequired'
                      ) as unknown as string,
                    }}
                    name="firstName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label={t('userSignUpPage.firstName') + '*'}
                        variant="filled"
                        error={!!error}
                        helperText={error ? error?.message : ' '}
                        fullWidth
                        sx={{
                          color: 'common.black',
                          borderColor: grey[300],
                        }}
                        {...field}
                      ></TextField>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    rules={{
                      required: t(
                        'validation.fieldRequired'
                      ) as unknown as string,
                    }}
                    name="lastName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label={t('userSignUpPage.lastName') + '*'}
                        variant="filled"
                        error={!!error}
                        helperText={error ? error?.message : ' '}
                        fullWidth
                        sx={{
                          color: 'common.black',
                          borderColor: grey[300],
                        }}
                        {...field}
                      ></TextField>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="mobileNumber"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label={t('userSignUpPage.phoneNumber')}
                        variant="filled"
                        error={!!error}
                        helperText={error ? error?.message : ' '}
                        fullWidth
                        sx={{
                          color: 'common.black',
                          borderColor: grey[300],
                        }}
                        {...field}
                      ></TextField>
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item container xs={12}>
              <Grid item>
                <Controller
                  control={control}
                  name="agreement"
                  rules={{
                    required: `${t(
                      'userSignUpPage.acceptTermsCheckbox.error'
                    )}`,
                  }}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox onChange={onChange} checked={value} />
                        }
                        label={
                          <Box>
                            <Typography>
                              {t('userSignUpPage.acceptTermsCheckbox.text')}
                              <Link
                                href={`${process.env.REACT_APP_REDIRECT_SEARCH_APP_URL}terms`}
                                variant="body1"
                                underline="none"
                                target="_blank"
                              >
                                {t('userSignUpPage.acceptTermsCheckbox.link')}
                              </Link>
                              .{' '}
                            </Typography>
                          </Box>
                        }
                      />
                      <Grow in={Boolean(error)}>
                        <FormHelperText error={Boolean(error)}>
                          {error?.message}
                        </FormHelperText>
                      </Grow>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
            <Grid item>
              <Typography variant="caption" sx={{ color: 'lightgrey' }}>
                This site is protected by reCAPTCHA and the Google Privacy
                Policy and Terms of Service apply.
              </Typography>
            </Grid>
            <Grid item container xs={12}>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    width: '100%',
                    textTransform: 'none',
                    minHeight: '50px',
                  }}
                >
                  {createUserMutation.isLoading ? (
                    <CircularProgress
                      sx={{
                        '& svg': {
                          color: 'background.paper',
                        },
                      }}
                      size={50}
                    />
                  ) : (
                    <Typography fontSize={20}>
                      {t('userSignUpPage.signUpButton')}
                    </Typography>
                  )}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Grow>
    </>
  )
}

export default SignupUser
